import { useEffect, useState, useCallback } from "react";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import {
  X, Save, Loader2, Send, FileText, Plus, Trash2, ExternalLink, Copy, Download, CheckCircle2,
} from "lucide-react";
import IntelligencePanel from "@/components/admin/IntelligencePanel";

const VERDICTS = [
  { v: "FAIR",               label: "Fair",                        hint: "Scope and cost appear reasonable" },
  { v: "SOMEWHAT_HIGH",      label: "Somewhat inflated",           hint: "Priced above typical but not egregious" },
  { v: "INFLATED",           label: "Significantly inflated",      hint: "Priced well above fair range" },
  { v: "UNNECESSARY",        label: "Work not required",           hint: "Evidence doesn't support proposed scope" },
  { v: "INSURANCE_RELEVANT", label: "May be insurance-relevant",   hint: "Consider claim before instructing" },
  { v: "INCONCLUSIVE",       label: "Inconclusive",                hint: "More info or inspection needed" },
];

const LEVEL_COLOURS = {
  red:   "bg-[#B91C1C] text-white",
  amber: "bg-[#B45309] text-white",
  green: "bg-[#064E2E] text-white",
};

const BLANK = {
  verdict: "INCONCLUSIVE",
  verdict_summary: "",
  scope_analysis: "",
  cost_assessment: "",
  risk_flags: [],
  insurance_considerations: "",
  next_step: "",
  reviewer_name: "Park Home Support",
};

// Stable per-row id for risk_flags. Index-as-key caused input focus loss + state
// bleed when adding/removing rows; this UID survives reorders and re-renders.
const _flagUid = () => `f_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
const _withFlagUids = (flags) =>
  (flags || []).map((f) => (f && f._uid ? f : { ...(f || {}), _uid: _flagUid() }));

export default function ReportEditor({ caseId, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [publicUrl, setPublicUrl] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");

  const load = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const { data } = await adminApi.get(`/cases/${caseId}/report`);
      setData(data);
      if (data.report) {
        setForm({ ...BLANK, ...data.report, risk_flags: _withFlagUids(data.report.risk_flags) });
        if (data.report.status === "published" && data.report.public_token) {
          const base = process.env.REACT_APP_BACKEND_URL;
          setPublicUrl(`${window.location.origin}/report/${data.report.public_token}`);
          setPdfUrl(`${base}/api/reports/${data.report.public_token}/pdf`);
        }
      } else {
        setForm(BLANK);
        setPublicUrl("");
        setPdfUrl("");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load report");
    } finally { setLoading(false); }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  function addFlag() {
    setForm((f) => ({ ...f, risk_flags: [...(f.risk_flags || []), { _uid: _flagUid(), level: "amber", text: "" }] }));
  }
  function removeFlag(i) {
    setForm((f) => ({ ...f, risk_flags: f.risk_flags.filter((_, idx) => idx !== i) }));
  }
  function updateFlag(i, k, v) {
    setForm((f) => ({ ...f, risk_flags: f.risk_flags.map((fl, idx) => idx === i ? { ...fl, [k]: v } : fl) }));
  }

  async function save(andPublish = false) {
    setSaving(true);
    try {
      // Strip the client-only _uid before sending to the backend.
      const payload = {
        ...form,
        risk_flags: (form.risk_flags || []).map(({ _uid, ...rest }) => rest),
      };
      await adminApi.post(`/cases/${caseId}/report`, payload);
      toast.success(andPublish ? "Report saved" : "Draft saved");
      if (andPublish) await publish();
      else await load();
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  async function publish() {
    if (!window.confirm("Publish this report? The customer will receive an email with the public link.")) return;
    setPublishing(true);
    try {
      const { data } = await adminApi.post(`/cases/${caseId}/report/publish`);
      setPublicUrl(`${window.location.origin}/report/${data.public_token}`);
      setPdfUrl(`${process.env.REACT_APP_BACKEND_URL}/api/reports/${data.public_token}/pdf`);
      toast.success("Report published — customer notified");
      await load();
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Publish failed");
    } finally { setPublishing(false); }
  }

  function copy(url) {
    navigator.clipboard.writeText(url);
    toast.success("Copied to clipboard");
  }

  if (!caseId) return null;
  const isPublished = data?.report?.status === "published";
  const caseMeta = data?.case;

  return (
    <div className="fixed inset-0 z-[70] bg-[#071A33]/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose} data-testid="report-editor">
      <div className="bg-white w-full max-w-4xl max-h-[94vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2DCD0] sticky top-0 bg-white z-10">
          <div>
            <p className="eyebrow">Quote Review Report</p>
            <h2 className="font-display text-xl text-[#071A33] mt-0.5">
              {caseMeta ? `${caseMeta.full_name} — ${caseMeta.park_name || "—"} (Plot ${caseMeta.plot_number || "—"})` : "Report"}
            </h2>
          </div>
          <button onClick={onClose} className="p-1 text-[#475569] hover:text-[#071A33]" data-testid="report-editor-close"><X size={20}/></button>
        </div>

        {loading || !data ? (
          <div className="p-10 flex items-center gap-2 text-[#475569]"><Loader2 className="animate-spin" size={16}/> Loading…</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Published banner */}
            {isPublished && (
              <div className="card-default border-l-4 border-[#064E2E] bg-[#F0FBF6]" data-testid="report-published-banner">
                <div className="flex flex-wrap gap-4 justify-between items-start">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#064E2E] mt-0.5" size={20}/>
                    <div>
                      <p className="font-display text-lg text-[#071A33]">Published</p>
                      <p className="text-sm text-[#475569]">Customer can view the report at the public URL below. Edits save as draft-over-published.</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => copy(publicUrl)} className="btn-outline h-9 py-0 px-3 text-xs"><Copy size={12}/> Copy link</button>
                    <a href={publicUrl} target="_blank" rel="noreferrer" className="btn-outline h-9 py-0 px-3 text-xs"><ExternalLink size={12}/> View</a>
                    <a href={pdfUrl} target="_blank" rel="noreferrer" className="btn-primary h-9 py-0 px-3 text-xs"><Download size={12}/> PDF</a>
                  </div>
                </div>
              </div>
            )}

            {/* Inline intelligence — surfaces operator profile, park profile, cost benchmarks,
                historical cases, contractor history and guidance while writing the report. */}
            {caseMeta && (
              <IntelligencePanel
                parkName={caseMeta.park_name}
                town={caseMeta.town_location}
                contractorName={caseMeta.contractor_name}
                typeOfWorks={caseMeta.type_of_works}
                description={caseMeta.description}
                excludeCaseId={caseMeta.id}
              />
            )}

            {/* Verdict */}
            <div>
              <label className="field-label">Verdict <span className="text-[#B88A2A]">*</span></label>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {VERDICTS.map((v) => (
                  <button
                    key={v.v}
                    type="button"
                    onClick={() => update("verdict", v.v)}
                    data-testid={`verdict-${v.v.toLowerCase()}`}
                    className={`text-left p-3 rounded-xl border transition ${form.verdict === v.v ? "border-[#064E2E] bg-[#064E2E]/5 ring-2 ring-[#064E2E]/20" : "border-[#D7E7DF] bg-white hover:border-[#064E2E]"}`}
                  >
                    <p className="font-medium text-sm text-[#071A33]">{v.label}</p>
                    <p className="text-xs text-[#475569] mt-0.5">{v.hint}</p>
                  </button>
                ))}
              </div>
            </div>

            <Section label="Verdict summary (1–2 sentences — this headlines the report)">
              <textarea className="field-input" rows={2} value={form.verdict_summary} onChange={(e) => update("verdict_summary", e.target.value)} data-testid="rf-verdict-summary"/>
            </Section>

            <Section label="1. Scope analysis" hint="What work is proposed vs what is actually needed based on the submitted evidence.">
              <textarea className="field-input" rows={4} value={form.scope_analysis} onChange={(e) => update("scope_analysis", e.target.value)} data-testid="rf-scope"/>
            </Section>

            <Section label="2. Cost assessment" hint="Fair range vs quoted value, with short commentary.">
              <textarea className="field-input" rows={4} value={form.cost_assessment} onChange={(e) => update("cost_assessment", e.target.value)} data-testid="rf-cost"/>
            </Section>

            {/* Risk flags */}
            <div>
              <label className="field-label">3. Risk flags</label>
              <p className="text-xs text-[#475569] mb-2">Red / amber / green bullets — anything the customer must know before deciding.</p>
              <div className="space-y-2">
                {(form.risk_flags || []).map((f, i) => (
                  <div key={f._uid || `legacy-${i}`} className="flex gap-2 items-start" data-testid={`rf-flag-${i}`}>
                    <select className="field-input h-11 w-28" value={f.level} onChange={(e) => updateFlag(i, "level", e.target.value)} data-testid={`rf-flag-level-${i}`}>
                      <option value="red">Red</option>
                      <option value="amber">Amber</option>
                      <option value="green">Green</option>
                    </select>
                    <div className={`h-11 w-14 rounded-xl flex items-center justify-center text-xs font-medium ${LEVEL_COLOURS[f.level] || LEVEL_COLOURS.amber}`}>
                      {(f.level || "amber").toUpperCase()}
                    </div>
                    <input className="field-input flex-1" placeholder="Risk description" value={f.text} onChange={(e) => updateFlag(i, "text", e.target.value)} data-testid={`rf-flag-text-${i}`}/>
                    <button type="button" onClick={() => removeFlag(i)} className="h-11 px-3 text-[#475569] hover:text-[#B91C1C]" data-testid={`rf-flag-remove-${i}`}><Trash2 size={16}/></button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={addFlag} className="mt-2 inline-flex items-center gap-1.5 text-sm text-[#064E2E] hover:text-[#0F766E] font-medium" data-testid="rf-add-flag">
                <Plus size={14}/> Add risk flag
              </button>
            </div>

            <Section label="4. Insurance considerations" hint="What may or may not be claimable, and what evidence to preserve.">
              <textarea className="field-input" rows={4} value={form.insurance_considerations} onChange={(e) => update("insurance_considerations", e.target.value)} data-testid="rf-insurance"/>
            </Section>

            <Section label="5. Recommended next step" hint="Concrete instruction. 1–3 sentences.">
              <textarea className="field-input" rows={3} value={form.next_step} onChange={(e) => update("next_step", e.target.value)} data-testid="rf-nextstep"/>
            </Section>

            <div className="grid sm:grid-cols-2 gap-4">
              <Section label="Reviewer name (appears on report)">
                <input className="field-input" value={form.reviewer_name} onChange={(e) => update("reviewer_name", e.target.value)} data-testid="rf-reviewer"/>
              </Section>
            </div>

            {/* Actions */}
            <div className="border-t border-[#E2DCD0] pt-5 flex flex-wrap justify-end gap-2 sticky bottom-0 bg-white py-3">
              <button onClick={onClose} className="btn-ghost">Close</button>
              <button onClick={() => save(false)} disabled={saving} className="btn-outline" data-testid="rf-save-draft-btn">
                {saving ? <><Loader2 className="animate-spin" size={14}/> Saving…</> : <><Save size={14}/> Save draft</>}
              </button>
              <button onClick={publish} disabled={publishing || !data?.report} className="btn-primary" data-testid="rf-publish-btn">
                {publishing ? <><Loader2 className="animate-spin" size={14}/> Publishing…</> : <><Send size={14}/> {isPublished ? "Re-publish & notify" : "Publish & notify customer"}</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ label, hint, children }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      {hint && <p className="text-xs text-[#475569] mb-2">{hint}</p>}
      {children}
    </div>
  );
}
